import React, { createContext, useContext, useMemo } from 'react';

import jira from 'src/lib/api/jira';
import { usePromise } from 'src/lib/hooks';

import { FormProvider, useForm as useRuleForm } from 'src/views/components/Form/form';

import { onValidate } from './validator';

const getAllFields = (metadata) => {
  if (!metadata || !metadata.projects) return [];
  const fields = {};
  metadata
    .projects
    .flatMap(p => p.issuetypes)
    .forEach(issueType => {
      Object.entries((issueType && issueType.fields) || {}).forEach(([apiName, field]) => {
        if (!fields[apiName]) {
          fields[apiName] = { ...field, issueTypeIds: [], apiName };
        }
        fields[apiName].issueTypeIds.push(issueType.id);
      });
    });

  return Object.values(fields);
};

const createValueSelectorContext = (selector) => {
  const Context = createContext(null);
  const safeSelector = (values) => {
    try {
      return selector(values);
    } catch (e) {
      return undefined;
    }
  };

  const Provider = ({ children }) => {
    const { values } = useRuleForm();
    return (
      <Context.Provider value={safeSelector(values)}>
        {children}
      </Context.Provider>
    );
  };

  return [Provider, () => useContext(Context)];
};

const [ProjectsContextProvider, useSelectedProjects] = createValueSelectorContext(value => value.config.projects);
const [ServicesContextProvider, useSelectedServices] = createValueSelectorContext(value => value.config.services);
const [IssueTypesContextProvider, useSelectedIssueTypes] = createValueSelectorContext(value => value.config.issue_types);
const IssuesMetadataContext = createContext(null);
const IssuesMetadataContextProvider = ({ children, provideEmptyMetadata }) => {
  const selectedProjects = useSelectedProjects();
  const selectedIssueTypes = useSelectedIssueTypes();
  const { loading, data, error } = usePromise(() => {
    if (provideEmptyMetadata) return Promise.resolve({});
    return jira.getCreateMetadata(selectedProjects, selectedIssueTypes);
  }, [provideEmptyMetadata, selectedProjects, selectedIssueTypes]);

  const contextValue = useMemo(() => ({
    metadata: data,
    allFields: getAllFields(data),
    loading,
    error,
  }), [loading, error, data]);
  return (
    <IssuesMetadataContext.Provider value={contextValue}>
      {children}
    </IssuesMetadataContext.Provider>
  );
};
const useIssuesMetadata = () => {
  return useContext(IssuesMetadataContext);
};


export const RuleFormContext = ({ children, initRule, onSave, debug, readonly }) => {
  return (
    <FormProvider initValues={initRule} onSave={onSave} onValidate={onValidate} debug={debug}>
      <ProjectsContextProvider>
        <ServicesContextProvider>
          <IssueTypesContextProvider>
            <IssuesMetadataContextProvider provideEmptyMetadata={readonly}>
              {children}
            </IssuesMetadataContextProvider>
          </IssueTypesContextProvider>
        </ServicesContextProvider>
      </ProjectsContextProvider>
    </FormProvider>
  );
};


export { useIssuesMetadata,
  useRuleForm,
  useSelectedIssueTypes,
  useSelectedProjects,
  useSelectedServices };
